import {
  CHECKLIST_ANSWERS,
  CHECKLIST_CATEGORIES,
  CHECKLIST_RESULTS,
  KYS_APPROVER_ROLES,
  POWER_TYPES,
  toHm,
  type ChecklistAnswer,
  type ChecklistAssignment,
  type ChecklistCategory,
  type ChecklistItem,
  type ChecklistRecipient,
  type ChecklistResult,
  type ChecklistTemplate,
  type KysApproverRole,
  type KysFormRef,
  type PowerType,
  type SiteShift,
} from "@fts/core";
import type {
  ChecklistAnswerRow,
  ChecklistFault,
  ChecklistPhoto,
  ChecklistSubmission,
  ChecklistToken,
  EffectiveChecklistRecipients,
  ShiftInput,
} from "./provider";

/** Row shapes and mappers of the checklist tables (supabase/migrations/20270222000001_checklists.sql). */

const pick = <T extends string>(list: readonly T[], v: string, fallback: T): T => ((list as readonly string[]).includes(v) ? (v as T) : fallback);
const numArr = (v: Array<number | string> | null | undefined): number[] => (v ?? []).map(Number).filter((n) => Number.isFinite(n));

export const TEMPLATE_COLUMNS = "id, tenant_id, system_category, name, name_en, source_template_id, updated_at, form_code, form_revision, form_revision_date, form_title, form_title_en, form_family";
export const ITEM_COLUMNS = "id, template_id, position, code, text_tr, text_en, critical, photo_required_on_fail, power_types, impact_first, allow_na";
export type TemplateRow = {
  id: string;
  tenant_id: string | null;
  system_category: string | null;
  name: string;
  name_en: string | null;
  source_template_id: string | null;
  updated_at: string;
  /** KYS form identity (ADR 0033); absent on older rows. */
  form_code?: string | null;
  form_revision?: string | null;
  form_revision_date?: string | null;
  form_title?: string | null;
  form_title_en?: string | null;
  form_family?: string | null;
};

/** KYS form identity of a template or a submission snapshot (null = not a KYS form). */
export function toFormRef(r: { form_code?: string | null; form_revision?: string | null; form_revision_date?: string | null; form_title?: string | null; form_title_en?: string | null; form_family?: string | null }): KysFormRef | null {
  if (!r.form_code || !r.form_revision) return null;
  return { code: r.form_code, revision: r.form_revision, revisionDate: r.form_revision_date ?? null, titleTr: r.form_title ?? "", titleEn: r.form_title_en ?? "", family: r.form_family ?? null };
}
export type ItemRow = {
  id: string;
  template_id: string;
  position: number;
  code: string;
  text_tr: string;
  text_en: string | null;
  critical: boolean;
  photo_required_on_fail: boolean;
  power_types: string[] | string | null;
  impact_first: boolean;
  allow_na?: boolean;
};

/** Postgres enum arrays may arrive as "{lpg,diesel}" text. */
function powerTypes(v: string[] | string | null): PowerType[] {
  const list = Array.isArray(v) ? v : typeof v === "string" ? v.replace(/^\{|\}$/g, "").split(",").filter(Boolean) : [];
  return list.filter((x): x is PowerType => (POWER_TYPES as readonly string[]).includes(x));
}

export const toItem = (r: ItemRow): ChecklistItem => ({
  id: r.id,
  templateId: r.template_id,
  position: Number(r.position),
  code: r.code,
  textTr: r.text_tr,
  textEn: r.text_en ?? "",
  critical: r.critical === true,
  photoRequiredOnFail: r.photo_required_on_fail === true,
  powerTypes: powerTypes(r.power_types),
  impactFirst: r.impact_first === true,
  allowNa: r.allow_na === true,
});

/** System template names are bilingual; `lang` picks the EN one when asked. */
export function toTemplate(r: TemplateRow, items: ItemRow[], lang: "tr" | "en" = "tr"): ChecklistTemplate {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    systemCategory: r.system_category ? pick<ChecklistCategory>(CHECKLIST_CATEGORIES, r.system_category, "counterbalance_electric") : null,
    name: lang === "en" && r.name_en ? r.name_en : r.name,
    sourceTemplateId: r.source_template_id,
    form: toFormRef(r),
    items: items
      .filter((i) => i.template_id === r.id)
      .map(toItem)
      .sort((a, b) => a.position - b.position),
    updatedAt: r.updated_at,
  };
}

export const ASSIGNMENT_COLUMNS = "tenant_id, category, template_id";
export type AssignmentRow = { tenant_id: string; category: string; template_id: string };
export const toAssignment = (r: AssignmentRow): ChecklistAssignment => ({
  tenantId: r.tenant_id,
  category: pick<ChecklistCategory>(CHECKLIST_CATEGORIES, r.category, "counterbalance_electric"),
  templateId: r.template_id,
});

export const SHIFT_COLUMNS = "id, tenant_id, site_id, name, start_time, end_time, weekdays, time_zone";
export type ShiftRow = { id: string; tenant_id: string; site_id: string; name: string; start_time: string; end_time: string; weekdays: Array<number | string> | null; time_zone: string };
export const toShift = (r: ShiftRow): SiteShift => ({
  id: r.id,
  tenantId: r.tenant_id,
  siteId: r.site_id,
  name: r.name,
  start: toHm(r.start_time),
  end: toHm(r.end_time),
  weekdays: numArr(r.weekdays),
  tz: r.time_zone,
});
export const shiftToRow = (i: ShiftInput) => ({
  name: i.name.trim(),
  start_time: i.start,
  end_time: i.end,
  weekdays: [...new Set(i.weekdays)].sort((a, b) => a - b),
  time_zone: i.tz,
});

export const RECIPIENT_COLUMNS = "id, tenant_id, site_id, user_id, email, label";
export type RecipientRow = { id: string; tenant_id: string; site_id: string; user_id: string | null; email: string | null; label: string | null };
export const toRecipient = (r: RecipientRow): ChecklistRecipient => ({ id: r.id, tenantId: r.tenant_id, siteId: r.site_id, userId: r.user_id, email: r.email, label: r.label });

export type EffectiveRecipientRow = { recipient_id: string; user_id: string | null; display_name: string | null; email: string | null; source_site_id: string };
export const toEffectiveRecipients = (rows: EffectiveRecipientRow[]): EffectiveChecklistRecipients => ({
  recipients: rows.map((r) => ({ recipientId: r.recipient_id, userId: r.user_id, displayName: r.display_name, email: r.email, sourceSiteId: r.source_site_id })),
  sourceSiteId: rows.find((r) => r.user_id !== null)?.source_site_id ?? null,
  externalSourceSiteId: rows.find((r) => r.user_id === null)?.source_site_id ?? null,
});

export const SUBMISSION_COLUMNS = "id, tenant_id, site_id, vehicle_id, template_name, category, shift_name, shift_start, submitted_at, operator_id, operator_unknown, result, impact_since_last, form_code, form_revision, form_revision_date, form_title, form_title_en, form_family, document_no, approved_at, approved_role";
export type SubmissionRow = {
  id: string;
  tenant_id: string;
  site_id: string | null;
  vehicle_id: string;
  template_name: string;
  category: string;
  shift_name: string | null;
  shift_start: string | null;
  submitted_at: string;
  operator_id: string | null;
  operator_unknown: boolean;
  result: string;
  impact_since_last: boolean;
  form_code?: string | null;
  form_revision?: string | null;
  form_revision_date?: string | null;
  form_title?: string | null;
  form_title_en?: string | null;
  form_family?: string | null;
  document_no?: number | null;
  approved_at?: string | null;
  approved_role?: string | null;
};
export const toSubmission = (r: SubmissionRow): ChecklistSubmission => ({
  id: r.id,
  tenantId: r.tenant_id,
  siteId: r.site_id,
  vehicleId: r.vehicle_id,
  templateName: r.template_name,
  category: pick<ChecklistCategory>(CHECKLIST_CATEGORIES, r.category, "counterbalance_electric"),
  shiftName: r.shift_name,
  shiftStart: r.shift_start,
  submittedAt: r.submitted_at,
  operatorId: r.operator_id,
  operatorUnknown: r.operator_unknown === true,
  result: pick<ChecklistResult>(CHECKLIST_RESULTS, r.result, "ok"),
  impactSinceLast: r.impact_since_last === true,
  form: toFormRef(r),
  documentNo: r.document_no ?? null,
  approvedAt: r.approved_at ?? null,
  approvedRole: r.approved_role && (KYS_APPROVER_ROLES as readonly string[]).includes(r.approved_role) ? (r.approved_role as KysApproverRole) : null,
});

export const ANSWER_COLUMNS = "id, submission_id, position, code, text_tr, text_en, critical, photo_required_on_fail, answer, note";
export type AnswerRow = {
  id: string;
  submission_id: string;
  position: number;
  code: string;
  text_tr: string;
  text_en: string | null;
  critical: boolean;
  photo_required_on_fail: boolean;
  answer: string;
  note: string | null;
};
export const toAnswer = (r: AnswerRow): ChecklistAnswerRow => ({
  id: r.id,
  submissionId: r.submission_id,
  position: Number(r.position),
  code: r.code,
  textTr: r.text_tr,
  textEn: r.text_en ?? "",
  critical: r.critical === true,
  photoRequiredOnFail: r.photo_required_on_fail === true,
  answer: pick<ChecklistAnswer>(CHECKLIST_ANSWERS, r.answer, "ok"),
  note: r.note,
});

export const PHOTO_COLUMNS = "id, submission_id, answer_id, storage_path, content_type";
export type PhotoRow = { id: string; submission_id: string | null; answer_id: string | null; storage_path: string; content_type: string };
export const toPhoto = (r: PhotoRow): ChecklistPhoto => ({ id: r.id, submissionId: r.submission_id, answerId: r.answer_id, path: r.storage_path, contentType: r.content_type });

export const FAULT_COLUMNS =
  "id, tenant_id, site_id, vehicle_id, submission_id, answer_id, item_code, text_tr, text_en, critical, note, status, created_at, resolved_by, resolved_at, resolution_note";
export type FaultRow = {
  id: string;
  tenant_id: string;
  site_id: string | null;
  vehicle_id: string;
  submission_id: string;
  answer_id: string;
  item_code: string;
  text_tr: string;
  text_en: string | null;
  critical: boolean;
  note: string | null;
  status: string;
  created_at: string;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
};
export const toFault = (r: FaultRow): ChecklistFault => ({
  id: r.id,
  tenantId: r.tenant_id,
  siteId: r.site_id,
  vehicleId: r.vehicle_id,
  submissionId: r.submission_id,
  answerId: r.answer_id,
  itemCode: r.item_code,
  textTr: r.text_tr,
  textEn: r.text_en ?? "",
  critical: r.critical === true,
  note: r.note,
  status: r.status === "resolved" ? "resolved" : "open",
  createdAt: r.created_at,
  resolvedBy: r.resolved_by,
  resolvedAt: r.resolved_at,
  resolutionNote: r.resolution_note,
});

export type TokenRow = { vehicle_id: string; token: string; rotated_at: string | null };
export const toToken = (r: TokenRow): ChecklistToken => ({ vehicleId: r.vehicle_id, token: r.token, rotatedAt: r.rotated_at });
